import { IComposedTx, ITxBuilderSign } from "@sundaeswap/core";
import type { Tx, TxComplete } from "lucid-cardano";
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { FreezerItem, PoolFragmentFragment } from "../../gql/generated/stats2.sdk";
import { IOrderTransactionErrorState } from "../OrderContext";

export enum EFeeTiers {
  STABLE = "0.05",
  POPULAR = "0.30",
  EXOTIC = "1.00",
  DECAYING = "decaying",
}

export enum ELiquidityFlowState {
  initial = "initial",
  preview = "preview",
  previewCancelled = "previewCancelled",
  submitting = "submitting",
  submittingCancelled = "submittingCancelled",
  success = "success",
  error = "error",
  reset = "reset",
}

export interface ILiquidityTransactionErrorState extends IOrderTransactionErrorState {}

export enum ELiquidityTransactionBuilderState {
  BUILD = "BUILD",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
}

export interface ILiquidityTransactionState {
  builtTx?: IComposedTx<Tx, TxComplete>;
  completeTx?: ITxBuilderSign<TxComplete>;
  error?: IOrderTransactionErrorState;
  state?: ELiquidityTransactionBuilderState;
}

export interface ILiquidityContext {
  handlers: {
    setActiveYieldFarmingItem: Dispatch<SetStateAction<FreezerItem | undefined>>;
    setLiquidityFlowState: Dispatch<SetStateAction<ELiquidityFlowState>>;
    setPool: Dispatch<SetStateAction<PoolFragmentFragment | undefined>>;
  };
  state: {
    activeYieldFarmingItem?: FreezerItem;
    liquidityFlowState: ELiquidityFlowState;
    pool?: PoolFragmentFragment;
  };
}

const defaultLiquidityContext: ILiquidityContext = {
  handlers: {
    setActiveYieldFarmingItem: () => {},
    setLiquidityFlowState: () => {},
    setPool: () => {},
  },
  state: {
    activeYieldFarmingItem: undefined,
    liquidityFlowState: ELiquidityFlowState.initial,
    pool: undefined,
  },
};

const LiquidityContext = createContext(defaultLiquidityContext);

export const useLiquidityContext = () => useContext(LiquidityContext);

/**
 * This context provider is used to manage the overall state of the liquidity related UIs.
 * Since our UIs are heavily dependent on the state of the active pool (and in some cases the active yield farming item),
 * we use this context to manage the state of these values and provide them to the UIs that need them.
 *
 * This also makes sure we don't have to rely on the `given` and `taken` values from our `<OrderContext />` since using them
 * also "pollutes" the URL with query parameters that are not needed for the liquidity UIs.
 *
 * Also, the `OrderContext` should only be tied to the order related UIs, and not the liquidity related UIs. Therefore, utilizing
 * even the `flowState` does not make sense for the liquidity UIs as we can manage the flow state of the liquidity UIs using this context.
 */
const LiquidityContextProvider: FC<PropsWithChildren> = ({ children }) => {
  /**
   * States accounting for all liquidity context values
   */
  const [activeYieldFarmingItem, setActiveYieldFarmingItem] = useState<FreezerItem>();
  const [pool, setPool] = useState<PoolFragmentFragment | undefined>();
  const [liquidityFlowState, setLiquidityFlowState] = useState(
    defaultLiquidityContext.state.liquidityFlowState
  );

  return (
    <LiquidityContext.Provider
      value={{
        handlers: {
          setActiveYieldFarmingItem,
          setLiquidityFlowState,
          setPool,
        },
        state: {
          activeYieldFarmingItem,
          liquidityFlowState,
          pool,
        },
      }}
    >
      {children}
    </LiquidityContext.Provider>
  );
};

export default LiquidityContextProvider;
